import React from 'react';
import { TrendingUp, IndianRupee, Briefcase, Rocket, Globe } from 'lucide-react';

const roles = [
  { title: 'AI Marketing Specialist',  salary: '₹6L – ₹18L /yr', freelance: '₹80K – ₹2L /mo',   img: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=900&h=700&fit=crop&q=80',  accent: 'from-violet-600/85' },
  { title: 'Performance Marketer',     salary: '₹5L – ₹15L /yr', freelance: '₹60K – ₹1.5L /mo', img: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=900&h=700&fit=crop&q=80',  accent: 'from-blue-600/85'   },
  { title: 'Video Editor (Cinematic)', salary: '₹4L – ₹12L /yr', freelance: '₹50K – ₹1.2L /mo', img: 'https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=900&h=700&fit=crop&q=80',  accent: 'from-orange-600/85' },
  { title: 'AI Prompt Engineer',       salary: '₹8L – ₹22L /yr', freelance: '₹1L – ₹2.5L /mo',  img: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=900&h=700&fit=crop&q=80',  accent: 'from-pink-600/85'   },
  { title: 'Content Creator (AI)',     salary: '₹5L – ₹14L /yr', freelance: '₹60K – ₹1.5L /mo', img: 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=900&h=700&fit=crop&q=80',  accent: 'from-emerald-600/85'},
  { title: 'AI Automation Expert',     salary: '₹7L – ₹20L /yr', freelance: '₹90K – ₹2L /mo',   img: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=900&h=700&fit=crop&q=80',  accent: 'from-yellow-600/85' },
  { title: 'Social Media Strategist',  salary: '₹4L – ₹11L /yr', freelance: '₹50K – ₹1L /mo',   img: 'https://images.unsplash.com/photo-1611926653458-09294b3142bf?w=900&h=700&fit=crop&q=80',  accent: 'from-fuchsia-600/85'}
];

const stats = [
  { icon: <Briefcase className="w-5 h-5" />, num: '50,000+', label: 'AI Jobs Posted in India (2026)' },
  { icon: <TrendingUp className="w-5 h-5" />, num: '+74%',   label: 'Average Salary Hike After AI Skills' },
  { icon: <Globe className="w-5 h-5" />,     num: '3.5M+',  label: 'Global Freelancers Earning via AI' },
  { icon: <Rocket className="w-5 h-5" />,    num: '85%',    label: 'Roles That Will Need AI by 2028' }
];

const scrollToForm = () => document.getElementById('enrollment-form')?.scrollIntoView({ behavior: 'smooth' });

const CareerOutcomes = () => (
  <section data-testid="career-outcomes" className="py-24 px-6 relative overflow-hidden" style={{ background: 'radial-gradient(ellipse at top, #0f1a2e 0%, #060814 60%, #000 100%)' }}>
    <div className="absolute top-10 left-1/3 w-[600px] h-[600px] bg-yellow-500/10 rounded-full blur-[160px]"></div>
    <div className="absolute bottom-10 right-1/3 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[140px]"></div>
    <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(251,191,36,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(251,191,36,0.4) 1px, transparent 1px)', backgroundSize: '80px 80px' }}></div>

    <div className="container mx-auto max-w-6xl relative z-10">
      <div className="text-center mb-14 animate-fade-up">
        <div className="inline-flex items-center gap-2 bg-yellow-500/15 border border-yellow-500/30 rounded-full px-4 py-1.5 mb-4">
          <IndianRupee className="w-4 h-4 text-yellow-300" />
          <span className="text-xs font-bold text-yellow-300 uppercase tracking-widest">Career Outcomes &amp; Earning Potential</span>
        </div>
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight">
          This Is Not Just a Course.<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-amber-400 to-orange-400">This Is Your Future Career.</span>
        </h2>
        <p className="text-base md:text-lg text-gray-300 mt-4 max-w-2xl mx-auto">
          Real jobs. Real money. Real freedom. AI-skilled professionals earn <span className="font-bold text-yellow-300">3x more</span> than their peers.
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
        {stats.map((s, i) => (
          <div key={i} className="bg-gradient-to-br from-yellow-500/10 to-orange-500/5 border border-yellow-500/30 rounded-2xl p-5 text-center backdrop-blur-sm hover-lift">
            <div className="w-10 h-10 rounded-lg bg-yellow-500/20 text-yellow-300 flex items-center justify-center mx-auto mb-3">{s.icon}</div>
            <p className="text-2xl md:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-br from-yellow-200 to-orange-400">{s.num}</p>
            <p className="text-xs text-gray-300 mt-1 leading-tight">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Roles grid — image rollover cards */}
      <h3 className="text-2xl md:text-3xl font-black text-white text-center mb-10">
        Roles You Can Step Into <span className="text-yellow-300">(After Completing Our Program)</span>
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {roles.map((r, i) => (
          <div
            key={i}
            data-testid={`role-card-${i}`}
            className="group relative rounded-2xl overflow-hidden border border-white/10 cursor-default min-h-[300px] hover-lift"
          >
            {/* Background image with Ken-Burns hover */}
            <img src={r.img} alt={r.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" decoding="async" fetchPriority="high" />

            {/* Color accent overlay (top) + dark gradient (bottom) */}
            <div className={`absolute inset-0 bg-gradient-to-b ${r.accent} via-black/40 to-black/95`}></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>

            {/* Floating sparkle accent on hover */}
            <div className="absolute top-4 right-4 w-2 h-2 rounded-full bg-yellow-300 shadow-[0_0_12px_rgba(251,191,36,0.8)] opacity-0 group-hover:opacity-100 transition-opacity"></div>

            {/* Content overlay */}
            <div className="relative h-full flex flex-col justify-end p-6">
              {/* Salary peek (default visible) */}
              <h4 className="text-2xl md:text-3xl font-black text-white leading-tight mb-3 drop-shadow-lg">{r.title}</h4>

              {/* Salary lines — always visible, but slide up + reveal a CTA on hover */}
              <div className="space-y-1.5 mb-2">
                <div className="flex items-center gap-2 text-sm">
                  <Briefcase className="w-4 h-4 text-yellow-300 flex-shrink-0" />
                  <span className="text-white/90">Full-time:</span>
                  <span className="font-black text-yellow-200">{r.salary}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Rocket className="w-4 h-4 text-emerald-300 flex-shrink-0" />
                  <span className="text-white/90">Freelance:</span>
                  <span className="font-black text-emerald-200">{r.freelance}</span>
                </div>
              </div>

              {/* Hidden CTA — slides up on hover */}
              <button
                onClick={scrollToForm}
                className="mt-3 self-start opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 inline-flex items-center gap-2 bg-yellow-400 text-black font-black text-xs px-4 py-2 rounded-lg shadow-lg"
              >
                Start This Career <TrendingUp className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Emotional CTA */}
      <div className="mt-14 text-center">
        <p className="text-lg md:text-xl text-white font-semibold mb-4">
          🔥 <span className="text-yellow-300">Upcoming Batch Filling Fast</span> — Reserve your seat in today's career economy.
        </p>
        <button
          onClick={scrollToForm}
          data-testid="career-outcomes-cta"
          className="glow-cta inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-black px-8 py-4 rounded-xl shadow-2xl hover:scale-105 transition-transform text-lg"
        >
          Book Free Career Counseling
          <TrendingUp className="w-5 h-5" />
        </button>
      </div>
    </div>
  </section>
);

export default CareerOutcomes;
